import React from 'react';
import PropTypes from 'prop-types';
import { MentionsInput, Mention } from 'react-mentions';
import Avatar from '@strava/ui/Avatar';

import I18n from 'utils/I18n';
import createNetworkingClient from 'utils/networking-client';

import defaultStyle from './defaultStyle';
import fetchAthletes from './cache';
import sortAndFilter from './utils';

import styles from './styles.scss';

class MentionableComment extends React.Component {
  state = {
    isSubmitting: false,
    mentionableAthletes: [],
    value: ''
  };

  componentDidMount() {
    fetchAthletes().then((mentionableAthletes) => {
      this.setState({ mentionableAthletes });
    });
  }

  submitComment = (e) => {
    e.preventDefault();

    const { entity, entityId } = this.props;
    const { value } = this.state;

    this.setState({ isSubmitting: true });
    createNetworkingClient()
      .post(`/feed/${entity.toLowerCase()}/${entityId}/comment`, {
        comment: value
      })
      .then((response) => {
        if (response && response.status === 200) {
          this.handleSubmitCommentComplete(response.data);
        } else {
          this.handleSubmitCommentFail();
        }
      })
      .catch(() => {
        this.handleSubmitCommentFail();
      });
  };

  handleSubmitCommentComplete = (data) => {
    const { entity, entityId, onSubmitSuccess } = this.props;

    this.setState({ value: '', isSubmitting: false });

    if (onSubmitSuccess) {
      onSubmitSuccess(data);
      return;
    }

    // Dispatch an event so that code outside or React can be executed
    // when a comment is created.
    const ev = new CustomEvent('commentCreatedFromReact', {
      detail: {
        response: data,
        entity,
        entityId
      }
    });
    document.dispatchEvent(ev);
  };

  handleSubmitCommentFail = () => {
    // TODO: handle error case
  };

  handleChange = (e, value) => {
    this.setState({ value });
  };

  nameFromId = (id) => {
    const { mentionableAthletes } = this.state;
    return mentionableAthletes.find((a) => a.id === parseInt(id, 10)).display;
  };

  filterMentionableAthletes = (query) => {
    const { mentionableAthletes } = this.state;
    const { suggestionLimit } = this.props;

    const results = mentionableAthletes.filter(
      (a) => a.display.toLowerCase().indexOf(query.toLowerCase()) >= 0
    );

    if (suggestionLimit) {
      return results.slice(0, suggestionLimit);
    }

    return results;
  };

  fetchEntityAthletes = (e) => {
    if (e.key === '@') {
      const { entity, entityId } = this.props;

      fetchAthletes({ entity, entityId }).then((mentionableAthletes) => {
        this.setState((prevState) => ({
          mentionableAthletes: sortAndFilter(
            prevState.mentionableAthletes.concat(mentionableAthletes)
          )
        }));
      });
    }
  };

  renderSuggestion = (entry) => (
    <div className={styles.suggestion}>
      <Avatar
        name={entry.display}
        src={entry.profile}
        size="small"
        type="athlete"
        style={{ marginRight: '8px' }}
      />
      <div className={styles.metaContainer}>
        <div className={styles.athleteName}>
          {entry.display}
          {entry.member_type && entry.member_type.length > 0 && (
            <div className="app-icon-wrapper">
              <div
                className={`app-icon icon-badge-${entry.member_type} icon-xs ${styles.memberIcon}`}
              />
            </div>
          )}
        </div>
        {entry.location && entry.location.length > 0 && (
          <div className={`text-caption2 ${styles.location}`}>
            {entry.location}
          </div>
        )}
      </div>
    </div>
  );

  render() {
    const { value, isSubmitting } = this.state;
    const {
      placeHolder,
      str,
      textAreaClasses,
      buttonClasses,
      buttonText,
      autoFocus
    } = this.props;

    const useButtonText =
      buttonText || I18n.t('templates.feed.comment_box.post');

    return (
      <>
        <MentionsInput
          allowSpaceInQuery={true}
          autoFocus={autoFocus}
          style={defaultStyle}
          classNames={{
            mentions: 'media-body',
            mentions__highlighter: styles.highlighter,
            mentions__suggestions: styles.suggestions,
            mentions__suggestions__list: styles.suggestionList,
            mentions__input: textAreaClasses
          }}
          value={value}
          placeholder={placeHolder}
          onChange={this.handleChange}
          onKeyDown={this.fetchEntityAthletes}
        >
          <Mention
            appendSpaceOnAdd={true}
            data={this.filterMentionableAthletes}
            displayTransform={this.nameFromId}
            markup="[strava://athletes/__id__]"
            renderSuggestion={this.renderSuggestion}
            className={styles.mentionedAthlete}
          />
        </MentionsInput>
        <div className="media-actions">
          <button
            type="button"
            className={`text-footnote ${buttonClasses}`}
            disabled={isSubmitting || value.length === 0}
            onClick={this.submitComment}
            {...str}
          >
            {useButtonText}
          </button>
        </div>
      </>
    );
  }
}

MentionableComment.defaultProps = {
  str: null,
  placeHolder: null,
  textAreaClasses: null,
  buttonClasses: null,
  buttonText: null,
  suggestionLimit: null,
  autoFocus: false,
  onSubmitSuccess: null
};

MentionableComment.propTypes = {
  entity: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  str: PropTypes.shape({
    'str-type': PropTypes.string.isRequired,
    'str-on': PropTypes.string.isRequired,
    'str-trackable-id': PropTypes.string.isRequired
  }),
  placeHolder: PropTypes.string,
  textAreaClasses: PropTypes.string,
  buttonClasses: PropTypes.string,
  buttonText: PropTypes.string,
  suggestionLimit: PropTypes.number,
  autoFocus: PropTypes.bool,
  onSubmitSuccess: PropTypes.func
};

export default MentionableComment;
